import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Building2, BookOpen, MapPin, Users, Search, Plus, X, 
  RefreshCw, ChevronDown, ChevronRight, UserPlus, UserMinus,
  Shield, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import PageHeader from '../components/Shared/PageHeader';
import EmptyState from '../components/ui/EmptyState';

const SuperAdminPanel = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedTab, setSelectedTab] = useState('sponsors');
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectUsers, setObjectUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/superadmin/overview/');
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Failed to load overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectUsers = async (type, id) => {
    try {
      const response = await api.get(`/api/superadmin/${type}/${id}/users/`);
      setObjectUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchAllUsers = async (search = '') => {
    try {
      const response = await api.get(`/api/superadmin/users/?search=${search}`);
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching all users:', error);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    if (selectedObject) {
      fetchObjectUsers(selectedTab, selectedObject.id);
    }
  }, [selectedObject, selectedTab]);

  useEffect(() => {
    if (showAddUser) {
      fetchAllUsers(userSearch);
    }
  }, [showAddUser, userSearch]);

  const handleSelectObject = (type, obj) => {
    const id = type === 'sponsors' ? obj.sponsor_id : obj.id;
    setSelectedTab(type);
    setSelectedObject({ ...obj, id, type });
    setShowAddUser(false);
  };

  const handleAddUser = async (userId) => {
    if (!selectedObject) return;
    setActionLoading(true);
    try {
      await api.post(`/api/superadmin/${selectedObject.type}/${selectedObject.id}/users/`, {
        user_ids: [userId]
      });
      toast.success('User added successfully');
      fetchObjectUsers(selectedObject.type, selectedObject.id);
      fetchOverview();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Failed to add user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedObject) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/superadmin/${selectedObject.type}/${selectedObject.id}/users/`, {
        data: { user_ids: [userId] }
      });
      toast.success('User removed successfully');
      fetchObjectUsers(selectedObject.type, selectedObject.id);
      fetchOverview();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Failed to remove user');
    } finally {
      setActionLoading(false);
    }
  };

  const getObjectName = (obj, type) => {
    if (type === 'sponsors') return obj.sponsor_name;
    if (type === 'studies') return obj.title || obj.code;
    if (type === 'sites') return `${obj.site_code} - ${obj.site_name}`;
    return 'Unknown';
  };

  const tabs = [
    { key: 'sponsors', label: 'Sponsors', icon: Building2, color: 'blue' },
    { key: 'studies', label: 'Studies', icon: BookOpen, color: 'green' },
    { key: 'sites', label: 'Sites', icon: MapPin, color: 'purple' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading user manager...</span>
      </div>
    );
  }

  // Check for non-superuser after hooks
  if (user && !user.is_superuser) {
    return (
      <div className="rounded-xl border border-danger-500/25 bg-danger-50 p-6 text-center dark:bg-danger-500/10">
        <Shield className="mx-auto mb-3 h-6 w-6 text-danger-500 dark:text-danger-400" />
        <p className="text-sm font-medium text-danger-700 dark:text-danger-400">Superuser access required</p>
      </div>
    );
  }

  const stats = [
    { label: 'Total Users', value: overview?.total_users || 0, icon: Users },
    { label: 'Sponsors', value: overview?.total_sponsors || 0, icon: Building2 },
    { label: 'Studies', value: overview?.total_studies || 0, icon: BookOpen },
    { label: 'Sites', value: overview?.total_sites || 0, icon: MapPin },
  ];

  return (
    <div>
      {/* Header */}
      <PageHeader
        icon={Shield}
        title="SuperAdmin Panel"
        subtitle="Manage user assignments across sponsors, studies, and sites"
      >
        <button
          onClick={fetchOverview}
          className="btn btn-secondary"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </PageHeader>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="stat-label">
              <stat.icon className="h-3.5 w-3.5 text-primary-500" />
              {stat.label}
            </div>
            <p className="stat-value">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Panel - Object List */}
        <div className="card overflow-hidden lg:col-span-4">
          {/* Tab Navigation */}
          <div className="tabs-list gap-0 px-2 pt-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setSelectedTab(tab.key);
                  setSelectedObject(null);
                }}
                className={`tab flex-1 justify-center ${selectedTab === tab.key ? 'tab-active' : ''}`}
              >
                <tab.icon className={`h-4 w-4 ${selectedTab === tab.key ? 'text-primary-600 dark:text-primary-400' : ''}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Object List */}
          <div className="max-h-[500px] overflow-y-auto">
            {overview?.[selectedTab]?.map((obj) => {
              const id = selectedTab === 'sponsors' ? obj.sponsor_id : obj.id;
              const isSelected = selectedObject?.id === id;
              return (
                <button
                  key={id}
                  onClick={() => handleSelectObject(selectedTab, obj)}
                  className={`flex w-full items-center justify-between border-b border-l-[3px] border-b-gray-100 px-4 py-3 text-left transition-colors dark:border-b-gray-700 ${
                    isSelected
                      ? 'border-l-primary-600 bg-primary-50 dark:border-l-primary-400 dark:bg-primary-900/20'
                      : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {getObjectName(obj, selectedTab)}
                    </p>
                    {selectedTab === 'sites' && obj.study__title && (
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{obj.study__title}</p>
                    )}
                    {selectedTab === 'studies' && obj.sponsor__sponsor_name && (
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{obj.sponsor__sponsor_name}</p>
                    )}
                  </div>
                  <div className="ml-2 flex items-center">
                    <span className="badge badge-gray">
                      {obj.user_count} {obj.user_count === 1 ? 'user' : 'users'}
                    </span>
                    {isSelected ? (
                      <ChevronRight className="ml-2 h-4 w-4 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <ChevronDown className="ml-2 h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel - User Management */}
        <div className="card overflow-hidden lg:col-span-8">
          {selectedObject ? (
            <>
              {/* Header */}
              <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {getObjectName(selectedObject, selectedObject.type)}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {objectUsers.length} user{objectUsers.length !== 1 ? 's' : ''} assigned
                  </p>
                </div>
                <button
                  onClick={() => setShowAddUser(!showAddUser)}
                  className={`btn flex-shrink-0 ${showAddUser ? 'btn-secondary' : 'btn-primary'}`}
                >
                  {showAddUser ? (
                    <>
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      <span>Add User</span>
                    </>
                  )}
                </button>
              </div>

              {/* Add User Panel */}
              {showAddUser && (
                <div className="border-b border-gray-200 bg-primary-50/60 px-6 py-4 dark:border-gray-700 dark:bg-primary-900/10">
                  <div className="relative mb-3">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users by email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="input pl-10 pr-4"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1.5">
                    {allUsers
                      .filter((u) => !objectUsers.some((ou) => ou.id === u.id))
                      .map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{u.email}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {u.role} {u.is_superuser && '• Superuser'} {u.is_internal && '• Internal'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleAddUser(u.id)}
                            disabled={actionLoading}
                            className="btn btn-secondary btn-sm flex-shrink-0"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            <span>Add</span>
                          </button>
                        </div>
                      ))}
                    {allUsers.filter((u) => !objectUsers.some((ou) => ou.id === u.id)).length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">No users found</p>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned Users List */}
              <div className="max-h-[400px] overflow-y-auto">
                {objectUsers.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No users assigned"
                    action={
                      <button
                        onClick={() => setShowAddUser(true)}
                        className="link text-sm"
                      >
                        Add the first user
                      </button>
                    }
                  />
                ) : (
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="th-cell">
                          Email
                        </th>
                        <th className="th-cell">
                          Role
                        </th>
                        <th className="th-cell">
                          Type
                        </th>
                        <th className="th-cell text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {objectUsers.map((u) => (
                        <tr key={u.id} className="tr-interactive">
                          <td className="td-cell whitespace-nowrap text-gray-900 dark:text-gray-100">
                            {u.email}
                          </td>
                          <td className="td-cell whitespace-nowrap text-gray-500 dark:text-gray-400">
                            {u.role || 'N/A'}
                          </td>
                          <td className="td-cell whitespace-nowrap">
                            <div className="flex gap-1">
                              {u.is_superuser && (
                                <span className="badge badge-warning">
                                  Superuser
                                </span>
                              )}
                              {u.is_internal && (
                                <span className="badge badge-info">
                                  Internal
                                </span>
                              )}
                              {!u.is_superuser && !u.is_internal && (
                                <span className="badge badge-gray">
                                  External
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="td-cell whitespace-nowrap text-right">
                            <button
                              onClick={() => handleRemoveUser(u.id)}
                              disabled={actionLoading}
                              aria-label="Remove user"
                              title="Remove user"
                              className="btn btn-sm btn-icon btn-danger-soft"
                            >
                              <UserMinus className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              icon={ChevronRight}
              title="Select an object"
              description="Choose a sponsor, study, or site to manage its users"
              className="min-h-[320px]"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
